import React, { useEffect, useRef, useState } from 'react';
import { SIGN_COLOR } from './history-utils';

// LR-498: a net-new, dependency-free time-series chart for the History detail header.
// Ported from the design handoff (MarginChart.dc.html) — pure inline SVG + React state,
// no chart library (the repo has none; only a hand-rolled CSS-bar AccrualChart). The
// palette hexes below ARE the lr.* token values (lr.pos #15803d, lr.neg #dc2626,
// lr.accent #2563eb, lr.fg #161a20) — SVG `fill`/`stroke` take raw colour strings, so
// they are kept literal here rather than as Tailwind classes, matching the reference.
//
// Two variants:
//  • 'line'  — a balance time-series (Cash Pool: cashpool_after at each posting),
//              with an area fill and an optional dashed zero reference line.
//  • 'bars'  — discrete per-run magnitude bars (MTM: each run's net), sign-coloured. NOT a
//              connected line: each MTM run's net is an incremental delta against a moving
//              baseline that resets every run, so a line would imply a false continuous trend.

export interface MarginChartDatum {
  /** x-axis label, e.g. "24 Jun". Data is chronological oldest → newest. */
  date: string;
  value: number;
  /** Tooltip headline, e.g. "+1,565,000" or "12.4M open". */
  label: string;
}

export interface MarginChartProps {
  data: MarginChartDatum[];
  /** Line/area accent (line variant). '#15803d' balance · '#2563eb' exposure. */
  accent: string;
  variant?: 'line' | 'bars';
  /** Draw a dashed zero reference + clamp the scale to include 0 (balance crosses 0). */
  zeroLine?: boolean;
  /** Gridline number formatting. */
  unit?: 'money' | 'mln';
  height?: number;
  ariaLabel?: string;
  /** bars only (T2-5): emphasise this bar (full opacity + a grey outline) and default the tooltip to
   *  it, so the operator always sees which run they selected — even when its bar is ~0. */
  highlightIndex?: number;
  /** bars only (T2-5): shade a ±MTA band from Y(+mta) to Y(−mta), marking which runs are large
   *  enough to actually transfer cash (above the band) vs economic noise (inside it). */
  mtaBand?: number;
  /**
   * LR-542 (feedback #63, variant A): 'neutral' keeps the tooltip headline in the accent-light
   * tint for BOTH signs. The billing-signed Cash Pool balance is a ledger figure that crosses
   * zero in normal operation — a red negative headline would reintroduce the "against us"
   * reading the flip removed from every other pool surface. Default 'signed' (MTM bars).
   */
  tipTone?: 'signed' | 'neutral';
}

const GRID = '#eef0f2';
const ZERO = '#c0c5cd';
const LABEL = '#a8aeb8';
const TOOLTIP_BG = '#161a20';
const TOOLTIP_DATE = '#9aa1ad';
// lr.warn-weak / lr.warn token values (SVG fill/stroke take literal colours), for the ±MTA band.
const WARN_WEAK = '#fdf3e6';
const WARN = '#b45309';
const HIGHLIGHT_STROKE = '#6b7280';

const POS = SIGN_COLOR.pos;
const NEG = SIGN_COLOR.neg;
const NEU = SIGN_COLOR.neutral;

const r1 = (x: number): number => Math.round(x * 10) / 10;

function makeFmt(unit: 'money' | 'mln') {
  return (v: number): string => {
    if (unit === 'mln') return `${v.toFixed(1)}M`;
    const s = v > 0 ? '+' : v < 0 ? '−' : '';
    const a = Math.abs(v);
    if (a >= 1e6) return `${s}${(a / 1e6).toFixed(1)}M`;
    if (a >= 1e3) return `${s}${(a / 1e3).toFixed(0)}K`;
    return `${s}${a.toFixed(0)}`;
  };
}

/** Measure the container width so the chart fills the (flexible) detail pane and the
 *  HTML tooltip stays pixel-aligned with the SVG points under any width. */
function useElementWidth(fallback: number): readonly [React.RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const cw = entries[0]?.contentRect.width;
      if (cw && cw > 0) setW(cw);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

export const MarginChart: React.FC<MarginChartProps> = ({
  data,
  accent,
  variant = 'line',
  zeroLine = false,
  unit = 'money',
  height = 152,
  ariaLabel,
  highlightIndex,
  mtaBand,
  tipTone = 'signed',
}) => {
  const [containerRef, measuredW] = useElementWidth(560);
  // Hover state; the resolved index (hi) falls back to highlightIndex, else the LAST point, so the
  // chart reads "live" and the selected run is pre-emphasised before any interaction.
  const [hover, setHover] = useState<number | null>(null);

  if (data.length === 0) return null;

  const W = Math.max(measuredW, 280);
  const H = height;
  const L = 58;
  const R = 16;
  const T = 14;
  const B = 28;
  const iw = W - L - R;
  const ih = H - T - B;
  const n = data.length;
  const isBars = variant === 'bars';
  const fmt = makeFmt(unit);

  const vals = data.map((d) => d.value);
  let mn = Math.min(...vals);
  let mx = Math.max(...vals);
  // Bars grow from a zero baseline; the line clamps to 0 only when zeroLine is on.
  if (zeroLine || isBars) {
    mn = Math.min(mn, 0);
    mx = Math.max(mx, 0);
  }
  const pad = (mx - mn) * 0.16 || 1;
  mn -= pad;
  mx += pad;

  // n < 3 (a 1- or 2-point series): inset the points so they sit centred rather than pinned to the
  // edges (a 2-pt balance line stretching edge-to-edge reads as a full trend it isn't). (T2-6)
  const X = (i: number): number =>
    n < 3 ? L + ((i + 1) / (n + 1)) * iw : L + (i / (n - 1)) * iw;
  const Y = (v: number): number => T + ((mx - v) / (mx - mn)) * ih;

  // Clamp to the current series length: hover/highlightIndex can outlive a range switch that shrinks
  // `data` (the chart is keyed by selection, not by range, so it isn't remounted), and a stale index
  // would blank the always-on tooltip until the next hover.
  const hi = Math.min(hover ?? highlightIndex ?? n - 1, n - 1);
  const points = data.map((d, i) => ({
    ...d,
    cx: r1(X(i)),
    cy: r1(Y(d.value)),
    r: i === hi ? 4.5 : 3,
  }));

  const baseY = zeroLine || isBars ? r1(Y(0)) : r1(T + ih);
  const linePts = points.map((p) => `${p.cx},${p.cy}`).join(' ');
  const areaPath = `M ${r1(X(0))},${baseY} ${points
    .map((p) => `L ${p.cx},${p.cy}`)
    .join(' ')} L ${r1(X(n - 1))},${baseY} Z`;
  const areaFill = accent === POS ? 'rgba(21,128,61,.08)' : 'rgba(37,99,235,.08)';

  // 3 evenly-spaced gridline values across the padded range.
  const gvals = [mx - pad * 0.5, (mx + mn) / 2, mn + pad * 0.5];
  const grids = gvals.map((v) => ({ y: r1(Y(v)), ty: r1(Y(v) + 3), label: fmt(v) }));

  // Dedupe the first/middle/last ticks by DATE STRING (not just by index) so a sparse series whose
  // ticks share a calendar day doesn't render "Jun 15 … Jun 15" twice. (T2-6)
  const midI = Math.floor((n - 1) / 2);
  const tickIdx = (n > 1 ? [0, midI, n - 1] : [0]).filter((v, i, a) => a.indexOf(v) === i);
  const xticks: { cx: number; date: string }[] = [];
  for (const i of tickIdx) {
    const date = points[i].date;
    if (xticks.length && xticks[xticks.length - 1].date === date) continue;
    xticks.push({ cx: points[i].cx, date });
  }

  const hp = points[hi];
  // Tooltip headline colour must agree with the mark it describes: bars are sign-coloured (a
  // positive run's bar is green), so its tooltip headline is light-green — not the accent's light
  // blue. A 'signed' line keeps the accent-light tint for positives and red for negatives; a
  // 'neutral' line (LR-542: the billing-signed Cash Pool balance, which crosses zero in normal
  // operation) keeps the accent-light tint for BOTH signs — the marks it describes carry no
  // sign colour either.
  const positiveTipColor = isBars ? '#86e0a8' : accent === POS ? '#86e0a8' : '#9ec5ff';
  const tip = hp
    ? {
        x: Math.min(Math.max(hp.cx - 48, 2), W - 104),
        y: Math.max(hp.cy - 48, 2),
        label: hp.label,
        date: hp.date,
        crossX: hp.cx,
        color:
          tipTone === 'neutral'
            ? positiveTipColor
            : hp.value < 0
              ? '#f7a8a8'
              : positiveTipColor,
      }
    : null;

  // Bars: half-slot wide, centred on each x, drawn from the zero baseline, sign-coloured.
  const barW = Math.max(6, Math.min(28, (iw / n) * 0.5));
  const barColor = (v: number): string => (v < 0 ? NEG : v > 0 ? POS : NEU);

  const label =
    ariaLabel ??
    `${variant === 'bars' ? 'Net per run' : 'Balance at each call'}, ${data[0].date} ${
      data[0].label
    } to ${data[n - 1].date} ${data[n - 1].label}`;

  return (
    <div ref={containerRef} className="relative w-full" style={{ height: H }}>
      <svg
        width={W}
        height={H}
        role="img"
        aria-label={label}
        style={{ display: 'block', overflow: 'visible' }}
      >
        {/* gridlines + right-anchored value labels */}
        {grids.map((g, i) => (
          <g key={`grid-${i}`}>
            <line x1={L} y1={g.y} x2={W - R} y2={g.y} stroke={GRID} strokeWidth={1} />
            <text
              x={L - 7}
              y={g.ty}
              textAnchor="end"
              style={{ font: "500 9.5px 'JetBrains Mono'", fill: LABEL }}
            >
              {g.label}
            </text>
          </g>
        ))}

        {/* dashed zero reference (line variant) */}
        {zeroLine && !isBars && (
          <line
            x1={L}
            y1={r1(Y(0))}
            x2={W - R}
            y2={r1(Y(0))}
            stroke={ZERO}
            strokeWidth={1}
            strokeDasharray="3 3"
          />
        )}

        {isBars ? (
          <>
            {/* ±MTA band (T2-5) — behind everything; runs whose bar clears the band actually transfer. */}
            {mtaBand != null &&
              mtaBand > 0 &&
              (() => {
                const top = Math.max(T, r1(Y(mtaBand)));
                const bot = Math.min(T + ih, r1(Y(-mtaBand)));
                const h = Math.max(0, bot - top);
                return (
                  <g pointerEvents="none">
                    <rect x={L} y={top} width={iw} height={h} fill={WARN_WEAK} />
                    <text
                      x={W - R - 2}
                      y={top + 11}
                      textAnchor="end"
                      style={{ font: '500 9.5px Inter', fill: WARN }}
                    >
                      ± MTA {fmt(mtaBand)}
                    </text>
                  </g>
                );
              })()}
            {/* zero baseline for the bars */}
            <line x1={L} y1={baseY} x2={W - R} y2={baseY} stroke={ZERO} strokeWidth={1} />
            {points.map((p, i) => {
              const top = Math.min(p.cy, baseY);
              const h = Math.max(2, Math.abs(p.cy - baseY));
              const highlighted = i === highlightIndex;
              return (
                <rect
                  key={`bar-${i}`}
                  x={r1(p.cx - barW / 2)}
                  y={r1(top)}
                  width={r1(barW)}
                  height={r1(h)}
                  rx={2}
                  fill={barColor(p.value)}
                  opacity={i === hi ? 1 : 0.82}
                  stroke={highlighted ? HIGHLIGHT_STROKE : 'none'}
                  strokeWidth={highlighted ? 1.5 : 0}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setHover(i)}
                  onMouseOver={() => setHover(i)}
                />
              );
            })}
            {/* A ~0 selected bar is nearly invisible even with the outline; mark its x on the baseline
                with a short dashed tick so it's always findable. The value lives in the tooltip
                (defaulted to this bar), so no duplicate label here. (T2-5) */}
            {highlightIndex != null &&
              points[highlightIndex] &&
              Math.abs(points[highlightIndex].cy - baseY) < 6 && (
                <line
                  x1={points[highlightIndex].cx}
                  y1={baseY - 9}
                  x2={points[highlightIndex].cx}
                  y2={baseY + 9}
                  stroke={HIGHLIGHT_STROKE}
                  strokeWidth={1}
                  strokeDasharray="2 2"
                  pointerEvents="none"
                />
              )}
          </>
        ) : (
          <>
            <path d={areaPath} fill={areaFill} />
            <polyline
              points={linePts}
              fill="none"
              stroke={accent}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {/* crosshair at the hovered x */}
            {tip && (
              <line
                x1={tip.crossX}
                y1={T}
                x2={tip.crossX}
                y2={T + ih}
                stroke={ZERO}
                strokeWidth={1}
                strokeDasharray="2 2"
              />
            )}
            {points.map((p, i) => (
              <circle
                key={`dot-${i}`}
                cx={p.cx}
                cy={p.cy}
                r={p.r}
                fill="#fff"
                stroke={accent}
                strokeWidth={2}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHover(i)}
                onMouseOver={() => setHover(i)}
              />
            ))}
          </>
        )}

        {/* x-tick labels (first / middle / last) */}
        {xticks.map((x, i) => (
          <text
            key={`xt-${i}`}
            x={x.cx}
            y={H - 8}
            textAnchor="middle"
            style={{ font: '500 9.5px Inter', fill: LABEL }}
          >
            {x.date}
          </text>
        ))}

        {/* A 1- or 2-point series is centred, not edge-to-edge; say so so it reads as intentional. (T2-6) */}
        {n < 3 && (
          <text
            x={(L + W - R) / 2}
            y={T + 2}
            textAnchor="middle"
            style={{ font: 'italic 9px Inter', fill: LABEL }}
          >
            limited history
          </text>
        )}
      </svg>

      {tip && (
        <div
          style={{
            position: 'absolute',
            left: tip.x,
            top: tip.y,
            background: TOOLTIP_BG,
            borderRadius: 6,
            padding: '6px 9px',
            pointerEvents: 'none',
            boxShadow: '0 4px 14px rgba(13,19,34,.28)',
            whiteSpace: 'nowrap',
          }}
        >
          <div style={{ font: "700 12px 'JetBrains Mono'", color: tip.color }}>{tip.label}</div>
          <div style={{ font: '500 10px Inter', color: TOOLTIP_DATE, marginTop: 1 }}>{tip.date}</div>
        </div>
      )}
    </div>
  );
};
